import { withAlpha, useAccentFn } from "@/lib/color";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { trPositionShort } from "@/lib/pesTranslations";
import AlboEmbed from "@/components/AlboEmbed";
import TabellinoModal from "@/components/TabellinoModal";
import CupAdminPanel from "@/components/CupAdminPanel";
import TavolinoBadge from "@/components/TavolinoBadge";
import { matchLegLabel } from "@/lib/competitions";
import { useAuth } from "@/context/AuthContext";

// Map cup slug → albo competition_key
const CUP_ALBO_MAP = {
    "coppa-italia-op": "coppa_italia",
    "copa-del-rey-op": "copa_del_rey",
    "fa-cup-op": "fa_cup",
    "coupe-de-france-op": "coppa_francia",
    "champions-league-op": "champions",
    "europa-league-op": "europa",
    "conference-league-op": "conference",
    "supercoppa-italiana-op": "supercoppa_italiana",
    "supercopa-espana-op": "supercoppa_spagna",
    "community-shield-op": "community_shield",
    "trophee-des-champions-op": "supercoppa_francia",
    "supercoppa-europea-op": "supercoppa_europea",
};

// Trophy hero images per cup slug (WebP)
const CUP_TROPHIES = {
    "champions-league-op": "/cups/champions.webp",
    "europa-league-op": "/cups/europa.webp",
    "conference-league-op": "/cups/conference.webp",
    "coppa-italia-op": "/cups/coppa-italia.webp",
    "copa-del-rey-op": "/cups/copa-del-rey.webp",
    "fa-cup-op": "/cups/fa-cup.webp",
    "coupe-de-france-op": "/cups/coupe-de-france.webp",
    "supercoppa-italiana-op": "/cups/supercoppa-italiana.webp",
    "supercopa-espana-op": "/cups/supercopa-espana.webp",
    "community-shield-op": "/cups/community-shield.webp",
    "trophee-des-champions-op": "/cups/trophee-des-champions.webp",
    "supercoppa-europea-op": "/cups/supercoppa-europea.webp",
};

const KIND_LABEL = {
    national: "// COPPA NAZIONALE",
    european: "// COPPA EUROPEA",
    supercup: "// SUPERCOPPA",
};

function fmtEur(n) {
    if (!n) return null;
    if (n >= 1_000_000) return `€${(n / 1_000_000).toFixed(1)}mln`;
    return `€${n}`;
}

export default function CoppaDetail() {
    const acc = useAccentFn();
    const { slug } = useParams();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [cup, setCup] = useState(null);
    const [scorers, setScorers] = useState(null);
    const [mvps, setMvps] = useState(null);
    const [gks, setGks] = useState(null);
    const [standings, setStandings] = useState(null);
    const [tab, setTab] = useState("bracket");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [openMatchId, setOpenMatchId] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const reload = () => setReloadKey((k) => k + 1);

    // Fetch inside the effect via promise callbacks (no synchronous setState).
    useEffect(() => {
        let active = true;
        Promise.all([
            api.get(`/cups/${slug}`),
            api.get(`/cups/${slug}/top-scorers`).catch(() => ({ data: null })),
            api.get(`/cups/${slug}/top-mvp`).catch(() => ({ data: null })),
            api.get(`/cups/${slug}/top-goalkeepers`).catch(() => ({ data: null })),
            api.get(`/cups/${slug}/standings`).catch(() => ({ data: null })),
        ]).then(([c, ts, tm, tg, st]) => {
            if (!active) return;
            setCup(c.data);
            setScorers(ts.data);
            setMvps(tm.data);
            setGks(tg.data);
            setStandings(st.data);
        }).catch((e) => {
            if (active) setErr(e.response?.data?.detail || "Errore");
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [slug, reloadKey]);

    if (loading) {
        return <div className="max-w-7xl mx-auto px-4 py-10 op-label text-t8f95a3 animate-pulse">// CARICAMENTO…</div>;
    }
    if (err || !cup) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-10">
                <div className="op-card p-6 text-tff4d4d">{err || "Errore"}</div>
            </div>
        );
    }

    const accent = acc(cup.accent_color);
    const teamsById = cup._teams_by_id || {};

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative" data-testid="coppa-detail">
            {/* Decorative background tied to cup */}
            <div
                aria-hidden
                className="fixed inset-0 pointer-events-none -z-10"
                style={{
                    background: `radial-gradient(ellipse at 50% -10%, ${withAlpha(accent, "1F")} 0%, transparent 55%)`,
                }}
            />
            <div
                aria-hidden
                className="absolute top-2 right-2 sm:right-8 text-[280px] sm:text-[400px] leading-none opacity-[0.04] pointer-events-none select-none"
            >
                {cup.flag}
            </div>

            <Link to="/coppe" className="op-label text-t8f95a3 hover:text-tffffff inline-block mb-3 relative">
                ← Tutte le coppe
            </Link>

            <div className="flex items-start gap-4 sm:gap-6 relative">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-2xl drop-shadow-[0_0_10px_rgba(255,255,255,0.15)]">{cup.flag}</span>
                        <div className="op-label" style={{ color: accent }}>
                            {KIND_LABEL[cup.kind] || "// COPPA"}
                        </div>
                    </div>
                    <h1 className="op-heading text-3xl sm:text-5xl mt-2" style={{ color: accent }}>
                        {cup.name}
                    </h1>
                    <p className="text-tc9ccd3 text-sm mt-3 max-w-2xl">{cup.description}</p>
                </div>
                {CUP_TROPHIES[slug] && (
                    <div
                        className="relative shrink-0 w-24 h-32 sm:w-32 sm:h-44 lg:w-40 lg:h-52 rounded-lg overflow-hidden border border-t22252a"
                        style={{
                            background: `radial-gradient(ellipse at center, ${withAlpha(accent, "33")} 0%, ${withAlpha(accent, "08")} 45%, rgb(var(--tc-0a0b0e)) 100%)`,
                        }}
                        data-testid={`cup-trophy-${slug}`}
                    >
                        <div
                            aria-hidden
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background: `radial-gradient(circle at center, ${withAlpha(accent, "55")} 0%, transparent 60%)`,
                                filter: "blur(14px)",
                                opacity: 0.5,
                            }}
                        />
                        <img
                            src={CUP_TROPHIES[slug]}
                            alt={`Trofeo ${cup.name}`}
                            loading="lazy"
                            className="relative z-10 w-full h-full object-contain p-2"
                            style={{ filter: `drop-shadow(0 0 10px ${withAlpha(accent, "55")})` }}
                        />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 relative">
                <Stat label="Squadre" value={cup.participants_count} />
                <Stat label="Turni" value={cup.rounds?.length || 0} />
                <Stat label="Montepremi" value={fmtEur(cup.montepremi) || "—"} accent={accent} />
                <Stat label="Vincitore" value={fmtEur(cup.prize_winner) || "—"} />
            </div>

            {/* Admin: gestione coppa (composizione + genera + avanza) */}
            {isAdmin && <CupAdminPanel cup={cup} onChanged={reload} />}

            {/* Qualified teams */}
            {cup.qualified_teams && cup.qualified_teams.length > 0 && (
                <section className="mt-8" data-testid="qualified-teams">
                    <div className="op-label mb-3" style={{ color: accent }}>// QUALIFICATE</div>
                    <div className="flex flex-wrap gap-2">
                        {cup.qualified_teams.map((q) => {
                            const t = teamsById[q.team_id];
                            return (
                                <div key={q.team_id} className="op-card p-2 flex items-center gap-2">
                                    {t?.logo_url && <img src={t.logo_url} alt="" className="w-5 h-5 object-contain" />}
                                    <span className="text-sm text-tffffff">{t?.name || q.team_id}</span>
                                    <span className="op-mono text-[10px] text-t8f95a3">→ {q.round_key}</span>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Tabs */}
            <div className="flex gap-1 mt-8 border-b border-t22252a overflow-x-auto relative" data-testid="coppa-tabs">
                <TabBtn active={tab === "bracket"} onClick={() => setTab("bracket")} testId="tab-bracket" label="Tabellone" accent={accent} />
                {cup.kind === "european" && (
                    <TabBtn active={tab === "standings"} onClick={() => setTab("standings")} testId="tab-standings" label="Classifica Gironi" accent="rgb(var(--tc-2bb6ff))" />
                )}
                <TabBtn active={tab === "scorers"} onClick={() => setTab("scorers")} testId="tab-scorers" label="Capocannonieri" accent="rgb(var(--tc-ff7a1a))" />
                <TabBtn active={tab === "mvp"} onClick={() => setTab("mvp")} testId="tab-mvp" label="MVP" accent="rgb(var(--tc-e8ff00))" />
                <TabBtn active={tab === "gk"} onClick={() => setTab("gk")} testId="tab-gk" label="Portieri" accent="rgb(var(--tc-00bcd4))" />
                <TabBtn active={tab === "albo"} onClick={() => setTab("albo")} testId="tab-albo" label="Albo d'Oro" accent="rgb(var(--tc-ffd700))" />
            </div>

            {tab === "bracket" && (
                <section className="mt-6" data-testid="bracket">
                    <div className="space-y-6">
                        {cup.rounds?.map((r) => (
                            <RoundCard key={r.key} round={r} accent={accent} teamsById={teamsById} onOpenMatch={(id) => setOpenMatchId(id)} isAdmin={isAdmin} slug={slug} onChanged={reload} />
                        ))}
                    </div>
                </section>
            )}
            {tab === "scorers" && <CupRanking data={scorers} statKey="goals" statLabel="Gol" />}
            {tab === "standings" && <GironiStandings data={standings} accent={accent} />}
            {tab === "mvp" && <CupRanking data={mvps} statKey="mvp_count" statLabel="MVP" />}
            {tab === "gk" && <CupRanking data={gks} statKey="clean_sheets" statLabel="Clean Sheet" secondaryKey="avg_rating" secondaryLabel="Voto medio" />}
            {tab === "albo" && (
                <AlboEmbed
                    competitionKeys={CUP_ALBO_MAP[slug] ? [CUP_ALBO_MAP[slug]] : []}
                    accent={accent}
                    emptyMessage="Nessun vincitore registrato per questa coppa."
                />
            )}

            {openMatchId && (
                <TabellinoModal
                    matchId={openMatchId}
                    onClose={() => setOpenMatchId(null)}
                    onSaved={() => { reload(); }}
                />
            )}
        </div>
    );
}

function TabBtn({ active, onClick, label, testId, accent = "rgb(var(--tc-2bb6ff))" }) {
    return (
        <button
            onClick={onClick}
            className={`op-heading text-base px-4 py-3 border-b-2 transition-colors duration-150 whitespace-nowrap ${
                active ? "text-tffffff" : "text-t8f95a3 hover:text-tffffff"
            }`}
            style={{ borderBottomColor: active ? accent : "transparent" }}
            data-testid={testId}
        >
            {label}
        </button>
    );
}

function CupRanking({ data, statKey, statLabel, assistKey, secondaryKey, secondaryLabel }) {
    if (!data) return <div className="op-card p-5 mt-6 text-t8f95a3 text-sm">Dati non disponibili.</div>;
    return (
        <div className="mt-6">
            {data.note && (
                <div className="op-card p-3 mb-4 text-xs text-tff7a1a border-tff7a1a/40" style={{ background: "rgba(255,122,26,0.05)" }}>
                    {data.note}
                </div>
            )}
            <div className="op-card overflow-hidden">
                {data.players.length === 0 ? (
                    <div className="p-5 text-t8f95a3 text-sm">Nessuna squadra qualificata ancora. Quando ci saranno, la classifica si popolerà.</div>
                ) : (
                    <div className="divide-y divide-t1a1d24">
                        {data.players.map((p) => (
                            <div key={`${p.name}-${p.team_id}`} className="flex items-center gap-3 p-3" data-testid={`cup-rank-${p.team_id}-${p.name}`}>
                                <div className="op-mono text-sm text-t5c6370 w-6 text-right">{p.rank}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-tffffff text-sm font-semibold truncate">{p.name}</div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        {p.team_logo && <img src={p.team_logo} alt="" className="w-3.5 h-3.5 object-contain" />}
                                        <Link to={`/squadre/${p.team_id}`} className="op-mono text-[10px] text-t8f95a3 hover:text-t2bb6ff truncate">
                                            {p.team_name}
                                        </Link>
                                        <span className="op-mono text-[10px] text-t5c6370">· {trPositionShort(p.position)}</span>
                                    </div>
                                </div>
                                {assistKey && (
                                    <div className="text-right shrink-0 hidden sm:block">
                                        <div className="op-mono text-sm text-tc9ccd3">{p[assistKey] ?? 0}</div>
                                        <div className="op-mono text-[9px] text-t5c6370 uppercase">Assist</div>
                                    </div>
                                )}
                                {secondaryKey && (
                                    <div className="text-right shrink-0 w-14" data-testid={`cup-rank-secondary-${p.team_id}-${p.name}`}>
                                        <div className="op-mono text-sm font-semibold text-t00bcd4">{p[secondaryKey] != null ? p[secondaryKey].toFixed(1) : "—"}</div>
                                        <div className="op-mono text-[9px] text-t5c6370 uppercase">{secondaryLabel}</div>
                                    </div>
                                )}
                                <div className="text-right shrink-0 w-10">
                                    <div className="op-mono text-sm text-t8f95a3">VG {p.vg}</div>
                                </div>
                                <div className="text-right shrink-0 w-14">
                                    <div className="op-mono text-lg font-bold text-tffffff">{p[statKey] ?? 0}</div>
                                    <div className="op-mono text-[9px] text-t5c6370 uppercase">{statLabel}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function GironiStandings({ data, accent }) {
    if (!data || !data.rows || data.rows.length === 0) {
        return <div className="op-card p-5 mt-6 text-t8f95a3 text-sm">Classifica non ancora disponibile. Genera la coppa e inserisci i risultati dei gironi.</div>;
    }
    return (
        <div className="op-card overflow-x-auto mt-6" data-testid="gironi-standings">
            <div className="px-4 py-2 op-label text-[10px] text-t8f95a3 border-b border-t22252a grid grid-cols-[2rem_1fr_2.5rem_2.5rem] landscape:grid-cols-[2rem_1fr_repeat(8,2.2rem)] sm:grid-cols-[2rem_1fr_repeat(8,2.2rem)] gap-1 items-center min-w-0 landscape:min-w-[40rem] sm:min-w-[40rem]">
                <span>#</span><span>Squadra</span>
                <span className="text-center" title="Partite giocate">PG</span>
                <span className="text-center hidden landscape:block sm:block" title="Vittorie">V</span>
                <span className="text-center hidden landscape:block sm:block" title="Pareggi">N</span>
                <span className="text-center hidden landscape:block sm:block" title="Sconfitte">P</span>
                <span className="text-center hidden landscape:block sm:block" title="Gol fatti">GF</span>
                <span className="text-center hidden landscape:block sm:block" title="Gol subiti">GS</span>
                <span className="text-center hidden landscape:block sm:block" title="Differenza reti">DR</span>
                <span className="text-center" title="Punti">Pt</span>
            </div>
            <div className="divide-y divide-t16181c">
                {data.rows.map((r) => (
                    <div
                        key={r.team_id}
                        className={`px-4 py-2 grid grid-cols-[2rem_1fr_2.5rem_2.5rem] landscape:grid-cols-[2rem_1fr_repeat(8,2.2rem)] sm:grid-cols-[2rem_1fr_repeat(8,2.2rem)] gap-1 items-center text-sm min-w-0 landscape:min-w-[40rem] sm:min-w-[40rem] ${r.qualified ? "bg-t2bb6ff/8" : ""}`}
                        data-testid={`standings-row-${r.rank}`}
                    >
                        <span className="op-mono" style={{ color: r.qualified ? accent : "rgb(var(--tc-5c6370))" }}>{r.rank}</span>
                        <Link to={`/squadre/${r.team_id}`} className="flex items-center gap-2 min-w-0 hover:text-t2bb6ff">
                            {r.team_logo && <img src={r.team_logo} alt="" className="w-5 h-5 object-contain" />}
                            <span className="text-tffffff truncate">{r.team_name}</span>
                        </Link>
                        <span className="op-mono text-center text-t8f95a3">{r.pg}</span>
                        <span className="op-mono text-center text-tc9ccd3 hidden landscape:block sm:block">{r.won}</span>
                        <span className="op-mono text-center text-tc9ccd3 hidden landscape:block sm:block">{r.drawn}</span>
                        <span className="op-mono text-center text-tc9ccd3 hidden landscape:block sm:block">{r.lost}</span>
                        <span className="op-mono text-center text-t8f95a3 hidden landscape:block sm:block">{r.gf}</span>
                        <span className="op-mono text-center text-t8f95a3 hidden landscape:block sm:block">{r.ga}</span>
                        <span className="op-mono text-center text-tc9ccd3 hidden landscape:block sm:block">{r.gd}</span>
                        <span className="op-mono text-center text-tffffff font-bold">{r.pts}</span>
                    </div>
                ))}
            </div>
            <div className="px-4 py-2 op-mono text-[10px] text-t8f95a3 border-t border-t22252a">
                Le prime 8 (evidenziate) si qualificano ai Quarti.
            </div>
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className="op-card p-3">
            <div className="op-label text-[10px]">{label}</div>
            <div className="op-mono text-lg mt-1" style={{ color: accent || "rgb(var(--tc-ffffff))" }}>
                {value}
            </div>
        </div>
    );
}

function RoundCard({ round, accent, teamsById, onOpenMatch, isAdmin, slug, onChanged }) {
    const expectedMatches = computeExpectedMatches(round);
    const matches = round.matches || [];

    return (
        <div className="op-card p-4" style={{ borderLeft: `3px solid ${accent}` }} data-testid={`round-${round.key}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div>
                    <div className="op-heading text-xl">{round.name}</div>
                    <div className="op-mono text-[10px] text-t8f95a3 mt-1">
                        {legLabel(round.leg)} · {round.teams_in} → {round.teams_out}
                        {round.prize > 0 && (
                            <span style={{ color: accent }}> · Premio {fmtEur(round.prize)}</span>
                        )}
                    </div>
                </div>
                <div className="op-mono text-xs text-t5c6370">
                    {matches.length} / {expectedMatches} partite
                </div>
            </div>

            {matches.length === 0 ? (
                <div className="grid sm:grid-cols-2 gap-2">
                    {Array.from({ length: expectedMatches }).map((_, i) => (
                        <EmptyMatchSlot key={i} accent={accent} />
                    ))}
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                    {matches.map((m) => (
                        <MatchCard key={m.id} match={m} accent={accent} teamsById={teamsById} onClick={() => onOpenMatch?.(m.id)} isAdmin={isAdmin} slug={slug} onChanged={onChanged} />
                    ))}
                </div>
            )}
        </div>
    );
}

function legLabel(leg) {
    return {
        "two_legs": "Andata e ritorno",
        "single": "Partita secca",
        "random_8": "8 partite casuali",
        "replay": "Replay",
    }[leg] || leg;
}

function computeExpectedMatches(round) {
    if (round.leg === "two_legs") return (round.teams_in / 2) * 2;
    if (round.leg === "single") return round.teams_in / 2;
    if (round.leg === "random_8") return Math.ceil((round.teams_in * 8) / 2);
    return Math.max(1, Math.floor(round.teams_in / 2));
}

function EmptyMatchSlot({ accent }) {
    return (
        <div
            className="p-3 border border-dashed border-t22252a rounded-none flex items-center justify-center op-mono text-[10px] text-t5c6370"
            style={{ minHeight: 56 }}
        >
            <span style={{ color: accent, opacity: 0.4 }}>—</span>
            <span className="mx-2">in attesa</span>
            <span style={{ color: accent, opacity: 0.4 }}>—</span>
        </div>
    );
}

function MatchCard({ match, accent, teamsById, onClick, isAdmin, slug, onChanged }) {
    const home = teamsById[match.home_team_id];
    const away = teamsById[match.away_team_id];
    const played = match.status === "played";
    const locked = match.window_open === false && match.date_from && !played;
    const [editingG, setEditingG] = useState(false);
    const [gVal, setGVal] = useState(match.giornata || "");
    const [savingG, setSavingG] = useState(false);

    const saveGiornata = async (e) => {
        e?.stopPropagation();
        const g = parseInt(gVal, 10);
        if (!g || g < 1 || g > 26) { setEditingG(false); return; }
        setSavingG(true);
        try {
            await api.put(`/cups/${slug}/matches/${match.id}/giornata`, { giornata: g });
            setEditingG(false);
            onChanged?.();
        } catch (err) {
            alert(err.response?.data?.detail || "Errore nel salvataggio della giornata");
        } finally {
            setSavingG(false);
        }
    };

    return (
        <div
            className="op-card bg-t0c0e11 p-3 cursor-pointer hover:border-t2bb6ff transition-colors"
            data-testid={`match-${match.id}`}
            onClick={onClick}
        >
            <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0 text-right">
                    {home?.logo_url && <img src={home.logo_url} alt="" className="w-5 h-5 object-contain inline mr-1.5" />}
                    <span className="text-sm text-tffffff">{home?.name || "—"}</span>
                </div>
                <div className="op-mono text-base font-bold w-14 text-center" style={{ color: accent }}>
                    {played ? `${match.home_score}:${match.away_score}` : "VS"}
                </div>
                <div className="flex-1 min-w-0 text-left">
                    <span className="text-sm text-tffffff">{away?.name || "—"}</span>
                    {away?.logo_url && <img src={away.logo_url} alt="" className="w-5 h-5 object-contain inline ml-1.5" />}
                </div>
            </div>
            <div className="flex items-center justify-center gap-2 mt-1.5 flex-wrap">
                {match.date_from && (
                    <span className="op-mono text-[9px] text-t5c6370" data-testid={`match-date-${match.id}`}>
                        {fmtDM(match.date_from)}
                    </span>
                )}
                {match.giornata && (
                    <span className="op-mono text-[9px] text-t5c6370">· G{match.giornata}</span>
                )}
                {match.leg && (
                    <span
                        className={`op-mono text-[9px] uppercase ${match.leg === "replay" ? "text-tffd700 font-bold" : "text-t5c6370"}`}
                        data-testid={`match-leg-${match.id}`}
                    >
                        · {match.leg === "replay" ? "⚔️ Bella" : matchLegLabel(match.leg)}
                    </span>
                )}
                {match.penalty_winner && (
                    <span className="op-mono text-[9px] text-tffd700" data-testid={`match-pens-${match.id}`}>
                        · d.c.r. {(match.penalty_winner === "home" ? home : away)?.name}
                    </span>
                )}
                {locked && (
                    <span className="op-mono text-[9px] text-tff7a1a" title={`Il tabellino si attiva il ${match.date_from}`}>
                        🔒 dal {fmtDM(match.date_from)}
                    </span>
                )}
                {match.auto_result && <TavolinoBadge idSuffix={match.id} definitive={match.is_definitive} />}
            </div>
            {isAdmin && (
                <div className="flex items-center justify-center gap-1.5 mt-2 pt-2 border-t border-t16181c" onClick={(e) => e.stopPropagation()}>
                    {editingG ? (
                        <>
                            <span className="op-label text-[9px] text-t5c6370">GIORNATA</span>
                            <input
                                type="number" min={1} max={26} value={gVal}
                                onChange={(e) => setGVal(e.target.value)}
                                className="w-14 bg-t0c0e11 border border-t22252a text-tffffff text-xs px-1.5 py-0.5 op-mono"
                                data-testid={`cup-giornata-input-${match.id}`}
                                autoFocus
                            />
                            <button onClick={saveGiornata} disabled={savingG}
                                className="op-label text-[9px] px-2 py-0.5 border border-t00ff66 text-t00ff66 disabled:opacity-50"
                                data-testid={`cup-giornata-save-${match.id}`}>
                                {savingG ? "…" : "OK"}
                            </button>
                            <button onClick={() => { setEditingG(false); setGVal(match.giornata || ""); }}
                                className="op-label text-[9px] px-2 py-0.5 border border-t22252a text-t5c6370">
                                ✕
                            </button>
                        </>
                    ) : (
                        <button onClick={() => setEditingG(true)}
                            className="op-label text-[9px] px-2 py-0.5 border border-t2bb6ff text-t2bb6ff"
                            data-testid={`cup-giornata-edit-${match.id}`}>
                            ✎ Sposta giornata
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function fmtDM(iso) {
    if (!iso) return "";
    const p = iso.split("-");
    return p.length === 3 ? `${p[2]}/${p[1]}` : iso;
}
